import React from 'react';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, IconButton, MenuItem, Select, Typography } from '@mui/material';

export interface PaginationData {
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

interface PaginationProps {
  data: PaginationData;
  onPageChange: (page: number) => void;
  onPerPageChange?: (perPage: number) => void;
  perPageOptions?: number[];
  /** Compact mode: just page nav, no range text or rows selector */
  compact?: boolean;
}

const DEFAULT_PER_PAGE_OPTIONS = [25, 50, 100];

export const Pagination: React.FC<PaginationProps> = ({
  data,
  onPageChange,
  onPerPageChange,
  perPageOptions = DEFAULT_PER_PAGE_OPTIONS,
  compact = false,
}) => {
  const { total, page, per_page, total_pages } = data;

  if (total === 0 || total_pages <= 1) return null;

  const start = (page - 1) * per_page + 1;
  const end = Math.min(page * per_page, total);

  if (compact) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="body2" sx={{ fontSize: '0.6875rem', color: 'text.secondary' }}>
          {start}–{end} of {total.toLocaleString()}
        </Typography>
        <IconButton
          size="small"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          sx={{ p: 0.25 }}
        >
          <ChevronLeftIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <IconButton
          size="small"
          disabled={page >= total_pages}
          onClick={() => onPageChange(page + 1)}
          sx={{ p: 0.25 }}
        >
          <ChevronRightIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        pt: 1.5,
      }}
    >
      <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
        {start}–{end} of {total.toLocaleString()}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {onPerPageChange && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography variant="body2" sx={{ fontSize: '0.6875rem', color: 'text.secondary' }}>
              Rows
            </Typography>
            <Select
              size="small"
              value={per_page}
              onChange={e => onPerPageChange(e.target.value as number)}
              sx={{
                fontSize: '0.75rem',
                height: 28,
                '& .MuiSelect-select': { py: 0.25, pr: 3 },
              }}
            >
              {perPageOptions.map(n => (
                <MenuItem key={n} value={n} sx={{ fontSize: '0.75rem' }}>
                  {n}
                </MenuItem>
              ))}
            </Select>
          </Box>
        )}

        <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary', mx: 0.5 }}>
          Page {page} of {total_pages}
        </Typography>

        <IconButton
          size="small"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          sx={{ p: 0.25 }}
        >
          <ChevronLeftIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <IconButton
          size="small"
          disabled={page >= total_pages}
          onClick={() => onPageChange(page + 1)}
          sx={{ p: 0.25 }}
        >
          <ChevronRightIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
    </Box>
  );
};
